import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { ProcedureType } from '@/types/cgt'

/** A window as the endpoints take it: ISO days, `to` EXCLUSIVE. */
export type DashboardQueryWindow = { from: string; to: string }

/** One day of a series; every day of the window is present, zeros included. */
export type DailyPoint = { day: string; count: number }

/** `GET /dashboard/procedures/summary` (z8nrz7eqyh). */
export interface ProceduresSummary {
  window: DashboardQueryWindow
  /** Runs still open at the window's end, by type. */
  open: { total: number; byType: { type: ProcedureType | 'unknown'; count: number }[] }
  /** Runs started in the window, by type. */
  started: { total: number; byType: { type: ProcedureType; total: number }[] }
  /** Runs completed in the window; `undated` are complete but cannot be placed in time. */
  completed: { total: number; undated: number }
  /** Started and completed per day (report 1.2's buckets). */
  trend: { day: string; started: number; completed: number }[]
}

/** `GET /dashboard/procedures/pipeline`: open runs by the step the board shows them on. */
export interface ProcedurePipeline {
  type: ProcedureType
  total: number
  stages: { name: string; count: number }[]
  /** Open runs with no step to place them on. */
  unplaced: number
}

export interface DashboardRunRef {
  id: string
  type: ProcedureType | 'unknown'
  templateName: string
  workOrderNumber: string | null
  patientMrn: string | null
}

export interface AtRiskProcedures {
  overdue: number
  dueSoon: number
  rows: (DashboardRunRef & { dueDate: string; reason: 'overdue' | 'due_soon'; days: number })[]
}

export interface UpcomingProcedures {
  days: number
  total: number
  byDay: { day: string; count: number }[]
  rows: (DashboardRunRef & { scheduledDate: string })[]
}

export interface PatientsSummary {
  window: DashboardQueryWindow
  total: number
  newInPeriod: number
  /** Patients with at least one open procedure. */
  active: number
  newByDay: DailyPoint[]
}

export interface FormsSummary {
  window: DashboardQueryWindow
  /** Signed off and waiting for their Review fields. */
  awaitingReview: number
  completedInPeriod: number
  completedByDay: DailyPoint[]
}

/** `GET /dashboard/process/steps` (z8nrz7eqym). */
export interface ProcessStepsSummary {
  window: DashboardQueryWindow
  assigned: number
  started: number
  /** Ended, waiting for the completion review. */
  pending: number
  completedInPeriod: number
  /** Assigned, not started, and past their planned start. */
  late: number
  lateRows: {
    id: string
    submissionId: string
    formName: string
    patientMrn: string | null
    plannedStart: string
    hoursLate: number
  }[]
  completedByDay: DailyPoint[]
}

/** `GET /dashboard/procedures/type-summary`: one procedure type's runs. */
export interface ProcedureTypeSummary {
  window: DashboardQueryWindow
  type: ProcedureType
  open: number
  startedInPeriod: number
  completedInPeriod: number
  /** Cancelled runs that started in the period. */
  cancelledInPeriod: number
  overdue: number
  /** Open and due within 7 days. */
  dueSoon: number
}

export interface KitBuildsSummary {
  window: DashboardQueryWindow
  inProgress: number
  createdInPeriod: number
  completedInPeriod: number
  /** Units, not builds: a batched build of 100 is 100 kits. */
  unitsCompletedInPeriod: number
  cancelledInPeriod: number
  completedByDay: DailyPoint[]
}

export interface PickListsSummary {
  open: { draft: number; open: number; inProgress: number; total: number }
  overdue: number
  shortages: number
  shortageRows: {
    pickListId: string
    pickListCode: string
    materialName: string
    qtyRequired: number
    onHandQty: number
    uom: string | null
    reason: 'SHORT' | 'NO_STOCK'
  }[]
}

// ─── Inventory, Products & Shipping (z8nrz7eqyq) ─────────────────────────────

export type LabelCount = { label: string; count: number }

export interface InventoryDashboard {
  window: DashboardQueryWindow
  lots: number
  lotsInStock: number
  onHandQty: number
  /** Lots in stock held in quarantine or their release workflow. */
  pendingLots: number
  expiredLots: number
  expiring30Lots: number
  shelfLife: LabelCount[]
  receivedByDay: DailyPoint[]
  stock: {
    out: number
    below: number
    ok: number
    noMinimum: number
    byCategory: { category: string; out: number; below: number; ok: number }[]
  }
  consumption: { totalQty: number; byDay: DailyPoint[]; topMaterials: LabelCount[] }
  materialsByStatus: LabelCount[]
}

export interface ProductsDashboard {
  window: DashboardQueryWindow
  createdInPeriod: number
  createdByDay: DailyPoint[]
  state: { inStorage: number; shipped: number; unplaced: number; total: number }
  byCatalog: LabelCount[]
  shipments: number
  shippedProducts: number
  shippedByDay: DailyPoint[]
  byPartner: LabelCount[]
  byCourier: LabelCount[]
}

// ─── Equipment, CAPA, Notifications, Audit & Integrations (z8nrz7eqyr) ──────

export interface EquipmentDashboard {
  window: DashboardQueryWindow
  total: number
  operational: number
  outOfService: number
  availabilityPct: number
  byType: LabelCount[]
  maintenance: {
    overdue: number
    dueNext7: number
    inProgress: number
    completedInPeriod: number
    onTimePct: number | null
    completedByDay: DailyPoint[]
    next: { id: string; equipmentLabel: string; eventTypeCode: string; dueDate: string; overdue: boolean }[]
  }
  reservations: { today: number; equipmentBookedToday: number; equipmentFreeToday: number }
}

export interface CapaDashboard {
  window: DashboardQueryWindow
  open: number
  started: number
  closed: number
  overdue: number
  openedInPeriod: number
  closedInPeriod: number
  meanDaysToClose: number | null
  openedByDay: DailyPoint[]
  closedByDay: DailyPoint[]
  openByType: LabelCount[]
  openAge: LabelCount[]
}

export interface NotificationsDashboard {
  window: DashboardQueryWindow
  unread: number
  inPeriod: number
  byCategory: LabelCount[]
  byDay: DailyPoint[]
  latest: { id: string; message: string; category: string; read: boolean; createdAt: string }[]
}

export interface AuditDashboard {
  window: DashboardQueryWindow
  entries: number
  byDay: DailyPoint[]
  byEvent: LabelCount[]
  byUser: LabelCount[]
  latest: { id: string; eventType: string; fieldLabel: string | null; changedBy: string; changedAt: string; submissionId: string }[]
}

export interface IntegrationsDashboard {
  window: DashboardQueryWindow
  received: number
  failed: number
  successPct: number | null
  byOutcome: LabelCount[]
  receivedByDay: DailyPoint[]
  failedByDay: DailyPoint[]
  latestFailures: { id: string; type: string; error: string | null; receivedAt: string }[]
}

async function request<T>(path: string): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, { headers: authHeaders() })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const parsed = JSON.parse(text) as { message?: string | string[] }
      if (parsed.message) {
        message = Array.isArray(parsed.message) ? parsed.message.join(', ') : parsed.message
      }
    } catch {
      /* leave raw text */
    }
    throw new Error(message || `API GET ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

const windowQuery = (w: DashboardQueryWindow) => new URLSearchParams({ from: w.from, to: w.to }).toString()

/** The dashboard widgets' data. Each endpoint answers one widget-sized question. */
export const dashboardApi = {
  proceduresSummary: (w: DashboardQueryWindow) =>
    request<ProceduresSummary>(`/dashboard/procedures/summary?${windowQuery(w)}`),
  pipeline: (type: ProcedureType = 'Transplant') =>
    request<ProcedurePipeline>(`/dashboard/procedures/pipeline?type=${encodeURIComponent(type)}`),
  atRisk: (limit = 10) => request<AtRiskProcedures>(`/dashboard/procedures/at-risk?limit=${limit}`),
  upcoming: (days = 7, limit = 10) =>
    request<UpcomingProcedures>(`/dashboard/procedures/upcoming?days=${days}&limit=${limit}`),
  patientsSummary: (w: DashboardQueryWindow) =>
    request<PatientsSummary>(`/dashboard/patients/summary?${windowQuery(w)}`),
  formsSummary: (w: DashboardQueryWindow) => request<FormsSummary>(`/dashboard/forms/summary?${windowQuery(w)}`),
  processSteps: (w: DashboardQueryWindow) =>
    request<ProcessStepsSummary>(`/dashboard/process/steps?${windowQuery(w)}&limit=10`),
  procedureTypeSummary: (type: ProcedureType, w: DashboardQueryWindow) =>
    request<ProcedureTypeSummary>(
      `/dashboard/procedures/type-summary?type=${encodeURIComponent(type)}&${windowQuery(w)}`,
    ),
  kitBuilds: (w: DashboardQueryWindow) => request<KitBuildsSummary>(`/dashboard/kits/builds?${windowQuery(w)}`),
  pickLists: () => request<PickListsSummary>('/dashboard/pick-lists?limit=10'),
  inventory: (w: DashboardQueryWindow) => request<InventoryDashboard>(`/dashboard/inventory?${windowQuery(w)}`),
  products: (w: DashboardQueryWindow) => request<ProductsDashboard>(`/dashboard/products?${windowQuery(w)}`),
  equipment: (w: DashboardQueryWindow) => request<EquipmentDashboard>(`/dashboard/equipment?${windowQuery(w)}&limit=8`),
  capa: (w: DashboardQueryWindow) => request<CapaDashboard>(`/dashboard/capa?${windowQuery(w)}`),
  notifications: (w: DashboardQueryWindow) => request<NotificationsDashboard>(`/dashboard/notifications?${windowQuery(w)}`),
  audit: (w: DashboardQueryWindow) => request<AuditDashboard>(`/dashboard/audit?${windowQuery(w)}`),
  integrations: (w: DashboardQueryWindow) => request<IntegrationsDashboard>(`/dashboard/integrations?${windowQuery(w)}`),
}
